/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 *
 * Design System — DataTable cell value resolution.
 *
 * Two jobs, both about surviving real server data rather than the tidy shape a
 * story passes in:
 *
 * 1. `readCellValue` resolves a column's value, following a dotted `field` path
 *    when one is given. Records APIs nest every user-defined field under
 *    `values`, so a column often needs `values.AnnualRevenue` rather than a
 *    top-level property.
 *
 * 2. `toDisplayText` turns whatever came back into something React can render.
 *    Field types include `object`, `complex`, `list` and `reference`, so a
 *    column without a `render` regularly receives an object or an array. Handing
 *    one to React throws "Objects are not valid as a React child", which unmounts
 *    the whole screen rather than just the cell — so anything non-primitive is
 *    stringified here instead.
 *
 * Deliberately self-contained: the design system imports nothing from `utils/`
 * or `store/`, so this mirrors `formatRecordValueForDisplay` from the app's
 * JsonUtil rather than importing it. Keep the two consistent if either changes.
 */

/** Shown when a value cannot be serialised at all (circular references). */
const UNSERIALISABLE = '[Unable to display]';

/**
 * Reads `path` off `source`, walking dots. Returns `undefined` at the first
 * missing link rather than throwing, since column definitions are often built
 * from server metadata and can outlive the field they name.
 */
const readPath = (source: unknown, path: string): unknown => {
  if (!path.includes('.')) {
    return source == null ? undefined : (source as Record<string, unknown>)[path];
  }

  return path.split('.').reduce<unknown>((current, segment) => {
    if (current === null || current === undefined) return undefined;

    return (current as Record<string, unknown>)[segment];
  }, source);
};

/**
 * The value a column points at. `field` wins over `key` when both are set, so
 * `key` stays free to be a stable column id that no row property has to match.
 */
export const readCellValue = <TData>(row: TData, key: string, field?: string): unknown => readPath(row, field ?? key);

/**
 * Best-effort text for a value React would otherwise reject.
 *
 * Arrays of primitives read as a list, because that is what multi-picklist and
 * multi-value text fields are. Everything else falls back to JSON, which is not
 * pretty but is honest — and the dev warning at the call site points at the
 * column that should have had a `render`.
 */
export const toDisplayText = (value: unknown): string => {
  if (typeof value === 'boolean') return String(value);

  if (value instanceof Date) {
    // Not formatted for a locale on purpose: a date column should declare a
    // `render` so it can be shown in the user's timezone. This only exists so a
    // forgotten one does not crash.
    return Number.isNaN(value.getTime()) ? '' : value.toISOString();
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return '';

    const allPrimitive = value.every(
      (item) => item === null || item === undefined || ['string', 'number', 'boolean'].includes(typeof item)
    );

    if (allPrimitive) {
      return value.map((item) => (item === null || item === undefined ? '' : String(item))).join(', ');
    }
  }

  try {
    return JSON.stringify(value) ?? UNSERIALISABLE;
  } catch {
    // Circular references, and anything else JSON.stringify refuses.
    return UNSERIALISABLE;
  }
};

/** Values that should show the table's `emptyValue` rather than any text. */
export const isBlankCellValue = (value: unknown): boolean => value === null || value === undefined || value === '';
